import React, { useState } from 'react';
import {
  Button, Input, Radio, Select, Space, Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import zoneStore from '../../Store';
import queueStore from '../../Store/queueStore';
import defaultValues from '../../../utils/defaultValues';
import { describeButtonTarget, findAncestors, isBrokenTarget } from '../../../utils/flowAnalysis';

// Намерение формулируем глаголом: из «Экраны зоны / Очереди / Спец. действия»
// не читалось, что произойдёт при нажатии кнопки.
const MODES = [
  { value: 'screen', label: 'Открыть экран', hint: 'Клиент перейдёт на выбранный экран' },
  { value: 'print', label: 'Выдать талон', hint: 'Кнопка напечатает талон в выбранную очередь' },
  { value: 'other', label: 'Другое', hint: 'Особые сценарии терминала' },
];

const detectMode = (path, params) => {
  if (params?.name === 'queue' && params.value) return 'print';
  if (path === 'print') return 'print';
  if (defaultValues.buttonActions.some((item) => item.id === path)) return 'other';
  return 'screen';
};

function ActionPicker({
  screen, value, params, nextScreen, onChange, onCreateScreen, onCreateQueue,
}) {
  const screens = zoneStore.useState((s) => s.config.screens);
  const queues = queueStore.useState((s) => s.queue) || [];

  const [mode, setMode] = useState(detectMode(value, params));
  const [search, setSearch] = useState('');

  const current = describeButtonTarget({ path: value, params, nextScreen }, screens, queues);
  const isBroken = isBrokenTarget(current);

  const match = (text) => (text || '').toLowerCase().includes(search.toLowerCase());

  // Переход на предка замкнул бы поток в цикл — такие экраны не предлагаем
  const forbidden = findAncestors(screens, screen.screenId).map((item) => item.screenId);

  const options = {
    screen: screens
      .filter((item) => item.screenId !== screen.screenId)
      .filter((item) => !forbidden.includes(item.screenId))
      .filter((item) => match(item.headTitle))
      .map((item) => ({
        key: item.screenId,
        label: item.headTitle || item.screenId,
        active: value === item.screenId,
        pick: () => onChange({ path: item.screenId, params: { name: 'queue', value: 0 } }),
      })),
    print: queues
      .filter((item) => match(item.fullname))
      .map((item) => ({
        key: `q-${item.id}`,
        label: item.fullname,
        active: Number(params?.value) === item.id,
        pick: () => onChange({ path: 'print', params: { name: 'queue', value: item.id } }),
      })),
    other: defaultValues.buttonActions
      .filter((item) => match(item.name))
      .map((item) => ({
        key: item.id,
        label: item.name,
        active: value === item.id,
        pick: () => onChange({ path: item.id, params: { name: 'queue', value: 0 } }),
      })),
  }[mode];

  const modeInfo = MODES.find((item) => item.value === mode);

  const emptyText = {
    screen: 'Подходящих экранов нет — создайте новый',
    print: 'Очередей нет — создайте новую',
    other: 'Ничего не найдено',
  }[mode];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Text strong>Что делает кнопка</Typography.Text>

      <Typography.Text type={isBroken ? 'danger' : 'secondary'}>
        {`Сейчас: ${current.label}`}
      </Typography.Text>

      <Radio.Group
        value={mode}
        onChange={(e) => { setMode(e.target.value); setSearch(''); }}
        optionType="button"
        buttonStyle="solid"
        options={MODES.map(({ value: v, label }) => ({ value: v, label }))}
      />

      <Typography.Text type="secondary" style={{ fontSize: 13, marginTop: -12 }}>
        {modeInfo.hint}
      </Typography.Text>

      {mode === 'screen' && (
        <Button icon={<PlusOutlined />} type="dashed" block onClick={onCreateScreen}>
          Новый экран
        </Button>
      )}
      {mode === 'print' && (
        <Button icon={<PlusOutlined />} type="dashed" block onClick={onCreateQueue}>
          Новая очередь
        </Button>
      )}

      {options.length > 4 && (
        <Input
          placeholder="Поиск по списку"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          allowClear
        />
      )}

      {mode === 'other' && value === 'externalIntegration' && (
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Экран после интеграции
          </Typography.Text>
          <Select
            style={{ width: '100%' }}
            value={nextScreen || undefined}
            placeholder="куда вернуться после успеха"
            status={nextScreen ? '' : 'error'}
            onChange={(screenId) => onChange({ nextScreen: screenId })}
            options={screens
              .filter((item) => item.screenId !== screen.screenId)
              .map((item) => ({
                value: item.screenId,
                label: item.headTitle || item.screenId,
              }))}
          />
        </div>
      )}

      <div style={{ maxHeight: 300, overflow: 'auto' }}>
        {options.length === 0 && (
          <Typography.Text type="secondary" style={{ fontSize: 13 }}>
            {emptyText}
          </Typography.Text>
        )}
        {options.map((item) => (
          <Button
            key={item.key}
            type={item.active ? 'primary' : 'default'}
            block
            style={{
              textAlign: 'left',
              marginBottom: 8,
              height: 'auto',
              minHeight: 40,
              padding: '8px 14px',
              whiteSpace: 'normal',
              lineHeight: 1.4,
            }}
            onClick={item.pick}
          >
            {item.label}
          </Button>
        ))}
      </div>
    </Space>
  );
}

export default ActionPicker;
